import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Form, Formik } from "formik";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import FFieldInput from "../../../components/Form/FFieldInput";
import { InputTypeE } from "../../../components/Form/types";
import { setPageTitle } from "../../../functions";
import { useGetDbData } from "../../../hooks/getDbData";
import { GlobalContext } from "../../../store";
import { UserI } from "../../../types";

interface InitialValuesI {
    email: string;
    password: string;
}

export const Login: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const store = useContext(GlobalContext);

    const { sendRequest: fetchDbData } = useGetDbData();

    const [loginState, setLoginState] = useState<boolean>();
    const [error, setError] = useState<{ message: string }>();

    const auth = getAuth();

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(t("Not a proper email").toString())
            .required(t("This field is mandatory").toString()),
        password: Yup.string()
            .min(8, t("Password too short").toString())
            .required(t("This field is mandatory").toString()),
    });

    const handleSubmit = (values: InitialValuesI) => {
        signInWithEmailAndPassword(auth, values.email, values.password)
            .then((userCredential) => {
                setError(undefined);
                setLoginState(true);

                const user = userCredential.user;
                fetchDbData<UserI[]>(
                    { table: "user", orderBy: "uid", where: user.uid },
                    (dbData) => {
                        if (dbData) {
                            store.setLoggedUserFn(dbData[0]);

                            setTimeout(() => {
                                navigate("/");
                            }, 1000);
                        }
                    }
                );
            })
            .catch((error) => {
                setLoginState(false);
                setError(error);
            });
    };

    useEffect(() => {
        setPageTitle(t("Login"));
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Card>
                <Card.Header>{t("Login")}</Card.Header>
                <Card.Body>
                    {typeof loginState !== "undefined" ? (
                        <Alert variant={loginState ? "success" : "warning"}>
                            {loginState ? (
                                t("Login successful")
                            ) : (
                                <>
                                    {t(
                                        "An error happened during login process"
                                    )}
                                    !
                                    <br />
                                    {t("Wrong email or password maybe")}?
                                    <br />
                                    {t("Try again, please")}...
                                </>
                            )}
                        </Alert>
                    ) : null}

                    <Formik
                        initialValues={{
                            email: "",
                            password: "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values: InitialValuesI) => {
                            handleSubmit(values);
                        }}
                    >
                        {(formApi) => (
                            <Form>
                                <FFieldInput
                                    formName={"email"}
                                    label={"E-mail"}
                                    type={InputTypeE.EMAIL}
                                    required
                                    className={"mb-3"}
                                />

                                <FFieldInput
                                    formName={"password"}
                                    label={t("Password")}
                                    type={InputTypeE.PASSWORD}
                                    required
                                    className={"mb-3"}
                                />

                                <Button
                                    type={"submit"}
                                    size={"lg"}
                                    disabled={
                                        !formApi.isValid ||
                                        Object.keys(formApi.errors).length >
                                            0 ||
                                        !formApi.dirty
                                    }
                                >
                                    {t("Log-in")}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </Card.Body>
            </Card>

            {error ? (
                <Alert variant={"info"} className={"mt-3"}>
                    {error.message}
                </Alert>
            ) : null}
        </>
    );
};
